import * as React from 'react'
import { FunctionComponent, ReactElement, useContext } from 'react'
import { Avatar, Link, makeStyles, mergeClasses, Text, tokens, Tooltip } from '@fluentui/react-components'
import { AttachArrowRightRegular, DeleteRegular, PersonAddRegular } from '@fluentui/react-icons'
import { useThemeClassName_unstable } from '@fluentui/react-shared-contexts'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import Card from '../../../../components/Card'
import PersonAvatar from '../../../../components/PersonAvatar'
import useAxios from '../../../../hooks/useAxios'
import useModules from '../../../../hooks/module/useModules'
import { FilePartyRoleDto, ModuleType } from '../../../../context/GlobalContext'
import AuthorizationContext from '../../../../context/AuthorizationContext'
import { FilePartyDto, MainDto } from '../../../../config/filePartyConfig'
import { IFileDto } from '../../../../config/fileConfig'
import { PartyType } from '../../../../components/Form/Party/PartyForm'
import AutoSavingInput, { InputStatus } from '../../../../components/Input/AutoSavingInput'
import PartyConnectButton from '../../../components/Button/PartyConnectButton'
import DeleteDialog from '../../../../components/Dialog/DeleteDialog'
import { ProofMeIcon } from '../../../../components/Icon/ProofMeIcon'
import { outlookDisplayNewMessageForm } from '../../../../helpers/outlookHelper'
import { createIdentificationRequest } from '../../../../components/List/FileParty/FilePartyList'
import { getPartyIcon } from '../../Party/PartyListItem'
import FilePartyActions from './FilePartyActions'
import { HammockToolbarButtonData } from '../../../../components/Overflow/OverflowToolbarPanel'

const getPartyEditUrl = (party: FilePartyDto, fileId: string): string => `/file/${fileId}/party/${(party.type || '').toLowerCase()}/${party.id}/edit`

const useStyles = makeStyles({
    card: {
        display: 'flex',
        flexDirection: 'column',
    },
    entity: {
        display: 'flex',
        flexDirection: 'column',
        // The pencil only appears on hover / keyboard focus (a11y: also on focus-within, not hover alone).
        '& [data-party-edit]': { opacity: 0 },
        '&:hover [data-party-edit]': { opacity: 1 },
        '&:focus-within [data-party-edit]': { opacity: 1 },
    },
    child: {
        marginLeft: tokens.spacingHorizontalXL,
        paddingLeft: tokens.spacingHorizontalS,
        borderLeft: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        columnGap: tokens.spacingHorizontalS,
        cursor: 'pointer',
        borderRadius: tokens.borderRadiusMedium,
        ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
        ':focus-visible': { outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}` },
    },
    nameBlock: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        flexGrow: 1,
    },
    name: {
        fontWeight: tokens.fontWeightSemibold,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    email: {
        color: tokens.colorNeutralForeground3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    editButton: { flexShrink: 0 },
    actions: {
        display: 'flex',
        // Align the action row under the name text (past the avatar + gap), both for the bedrijf and its contacts.
        marginLeft: tokens.spacingHorizontalXL,
    },
    // Ref-forwarding wrapper so OverflowItem can measure custom render items (popover/link/dialog) without warning.
    actionItem: {
        display: 'inline-flex',
        alignItems: 'center',
    },
})

type PartyEntityRowProps = {
    party: FilePartyDto
    file: IFileDto
    roles: FilePartyRoleDto[]
    // Seed for the avatar colour so every party of the same soort shares one colour (derived from the role name).
    colorSeed: string
    reset?: () => void
    isChild?: boolean
}

/**
 * One party entity (a bedrijf or a contactpersoon). The avatar + name + email area is a single button that opens
 * the (existing) edit screen; the reference field and action row live outside that button so they don't navigate.
 */
const PartyEntityRow: FunctionComponent<PartyEntityRowProps> = ({ party, file, roles, colorSeed, reset, isChild }) => {
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const history = useHistory()
    const axios = useAxios()
    const modules = useModules()
    const { host: currentHost } = useContext(AuthorizationContext)
    const themeClassName = useThemeClassName_unstable()

    const isLegalPerson = party.type === PartyType.Legal
    // Always a type icon (bedrijf/persoon/verified), coloured by the soort so a whole role shares one colour.
    const avatarIcon = getPartyIcon(party)
    const displayName = party.displayName || translate('file.pivot.party.card.noname')

    const openEdit = () => history.push(getPartyEditUrl(party, file.id))
    const onHeaderKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openEdit()
        }
    }

    const proofMeClick = () => {
        if (party.identificationVerified === true) {
            window.open(`https://dashboard.proofme.id/dashboard/identifications/${party.identificationRequestExternalId}`, '_blank')
        } else {
            createIdentificationRequest(axios, currentHost?.RowKey, party)
                .then(() => axios.get(`/party/${party.id}/identification/message`))
                .then((message: { data: { body: string; subject: string } }) =>
                    outlookDisplayNewMessageForm({
                        toRecipients: (party.email || '').split(/[,;]/).map(email => ({
                            displayName: party.displayName,
                            emailAddress: email?.trim(),
                        })) as Office.EmailAddressDetails[],
                        subject: message.data.subject,
                        htmlBody: message.data.body?.replaceAll('<br/>', '\n'),
                    }),
                )
        }
    }

    const proofMeTooltip =
        party.identificationVerified === false
            ? translate('party.proofme.create')
            : party.identificationVerified === true
              ? translate('party.proofme.view')
              : translate('party.proofme.resend')

    // Add-contactpersoon is the first (leading) action for a bedrijf; ProofMe and delete trail the standard actions.
    // Each keeps its own UI (popover/link/dialog) via `render`; icon+title feed the overflow menu fallback.
    const leadingButtons: HammockToolbarButtonData[] = isLegalPerson
        ? [
              {
                  value: 'add-contact',
                  icon: <PersonAddRegular />,
                  title: translate('file.pivot.party.add.contact'),
                  render: () => (
                      <span className={styles.actionItem}>
                          <PartyConnectButton iconOnly fileId={file.id} parent={party as MainDto} roles={roles} reset={reset} />
                      </span>
                  ),
              },
          ]
        : []

    const trailingButtons: HammockToolbarButtonData[] = [
        ...(modules.has(ModuleType.ProofMe) && party.type === PartyType.Natural
            ? [
                  {
                      value: 'proofme',
                      icon: <ProofMeIcon filled={party.identificationVerified === true} />,
                      title: proofMeTooltip,
                      render: () => (
                          <span className={styles.actionItem}>
                              <Tooltip relationship="label" content={proofMeTooltip} withArrow>
                                  <Link onClick={proofMeClick}>
                                      <ProofMeIcon filled={party.identificationVerified === true} />
                                  </Link>
                              </Tooltip>
                          </span>
                      ),
                  } as HammockToolbarButtonData,
              ]
            : []),
        {
            value: 'delete',
            icon: <DeleteRegular />,
            title: translate('file.pivot.party.delete.title'),
            render: () => (
                <span className={styles.actionItem}>
                    <DeleteDialog
                        className={themeClassName}
                        title={translate(`file.pivot.party.delete.${isLegalPerson ? 'legal' : 'natural'}.title`)}
                        content={translate(`file.pivot.party.delete.${isLegalPerson ? 'legal' : 'natural'}.subtitle`, { name: displayName })}
                        onDelete={setOpen =>
                            axios.delete(`/file-party/${party.pivotId}`).then(() => {
                                reset?.()
                                setOpen(false)
                            })
                        }
                        triggerButtonProps={{
                            children: undefined,
                        }}
                    />
                </span>
            ),
        },
    ]

    return (
        <div className={mergeClasses(styles.entity, isChild && styles.child)}>
            <div className={styles.header} role="button" tabIndex={0} onClick={openEdit} onKeyDown={onHeaderKeyDown}>
                {isLegalPerson ? (
                    <Avatar color="colorful" idForColor={colorSeed} icon={avatarIcon} size={24} />
                ) : (
                    <PersonAvatar name={displayName} color="colorful" idForColor={colorSeed} icon={avatarIcon} size={24} />
                )}
                <div className={styles.nameBlock}>
                    <Tooltip relationship="label" content={displayName} withArrow>
                        <Text className={styles.name}>{party.displayName || <em>{translate('file.pivot.party.card.noname')}</em>}</Text>
                    </Tooltip>
                    {party.email && (
                        <Text size={200} className={styles.email}>
                            {party.email}
                        </Text>
                    )}
                </div>
            </div>

            {isLegalPerson && (
                <AutoSavingInput
                    contentBefore={
                        <div>
                            <AttachArrowRightRegular />
                            {party.reference && `${party.reference} - `}
                        </div>
                    }
                    appearance="underline"
                    required={party.fileReferenceRequired}
                    value={party.fileReference}
                    save={(value: string) =>
                        axios.put(`/file-party/${party.pivotId}`, { fileReference: value }).then(() => {
                            party.fileReference = value
                            return InputStatus.Success
                        })
                    }
                    placeholder={translate('party.reference.placeholder')}
                />
            )}

            <div className={styles.actions}>
                <FilePartyActions party={party} file={file} leadingButtons={leadingButtons} trailingButtons={trailingButtons} />
            </div>
        </div>
    )
}

export type FilePartyCardProps = {
    party: MainDto
    file: IFileDto
    roles: FilePartyRoleDto[]
    // Name of the party's soort (role); used to colour the avatars so a whole soort shares one colour.
    roleName: string
    reset?: () => void
}

/**
 * A single party card: one bedrijf with its contactpersonen nested (indented) underneath.
 */
export default function FilePartyCard(props: FilePartyCardProps): ReactElement {
    const styles = useStyles()

    return (
        <Card className={styles.card}>
            <PartyEntityRow party={props.party} file={props.file} roles={props.roles} colorSeed={props.roleName} reset={props.reset} />
            {(props.party.children || []).map(child => (
                <PartyEntityRow
                    key={child.pivotId}
                    party={child}
                    file={props.file}
                    roles={props.roles}
                    colorSeed={props.roleName}
                    reset={props.reset}
                    isChild
                />
            ))}
        </Card>
    )
}
